import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors } from './theme';

// ── Modern audio play / progress bar ─────────────────────────────────────────
// Scrubbable timeline that works with the chunk-streaming useTTS hook.
//
// Props (all from useTTS `progress` + `seek`):
//   current        seconds elapsed
//   duration       seconds currently loaded (grows while streaming)
//   buffering      true while more chunks are still incoming, including the
//                  wait for the first one
//   durationKnown  true once `duration` is final
//   chunksLoaded   number of chunks received so far
//   onSeek(t)      seek to absolute time t (seconds)
//   disabled       render inert (greyed) when there's nothing to scrub
//
// Interactions: click, pointer-drag, mouse-wheel ("scroll"), and arrow keys.

export const fmt = s => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const AudioPlayBar = ({
  current = 0,
  duration = 0,
  buffering = false,
  durationKnown = false,
  chunksLoaded = 0,
  onSeek,
  disabled = false,
}) => {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  // While dragging we show the local scrub position for instant feedback.
  const [scrubX, setScrubX] = useState(null);

  const hasTimeline = duration > 0 && chunksLoaded > 0;
  const inert = disabled || !hasTimeline;

  const liveFraction =
    duration > 0 ? Math.min(1, Math.max(0, current / duration)) : 0;
  const fraction = scrubX != null ? scrubX : liveFraction;
  const pct = `${(fraction * 100).toFixed(2)}%`;

  const fractionFromEvent = useCallback(e => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    return Math.min(1, Math.max(0, x / rect.width));
  }, []);

  const commitSeek = useCallback(
    f => {
      if (onSeek && duration > 0) onSeek(f * duration);
    },
    [onSeek, duration]
  );

  const onPointerDown = e => {
    if (inert) return;
    e.preventDefault();
    const el = trackRef.current;
    try {
      el?.setPointerCapture?.(e.pointerId);
    } catch {
      /* noop */
    }
    const f = fractionFromEvent(e);
    setDragging(true);
    setScrubX(f);
  };

  const onPointerMove = e => {
    if (!dragging) return;
    setScrubX(fractionFromEvent(e));
  };

  const endDrag = e => {
    if (!dragging) return;
    const f = scrubX != null ? scrubX : fractionFromEvent(e);
    setDragging(false);
    setScrubX(null);
    commitSeek(f);
  };

  // Release the drag even if the pointer is lifted outside the track.
  useEffect(() => {
    if (!dragging) return;
    const move = e => setScrubX(fractionFromEvent(e));
    const up = e => endDrag(e);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging, scrubX]);

  const onWheel = e => {
    if (inert) return;
    e.preventDefault();
    const step = 5; // seconds per wheel notch
    const dir = e.deltaY > 0 || e.deltaX > 0 ? 1 : -1;
    const next = Math.min(duration, Math.max(0, current + dir * step));
    commitSeek(duration > 0 ? next / duration : 0);
  };

  const onKeyDown = e => {
    if (inert) return;
    let next = current;
    if (e.key === 'ArrowRight') next = current + 5;
    else if (e.key === 'ArrowLeft') next = current - 5;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = duration;
    else return;
    e.preventDefault();
    next = Math.min(duration, Math.max(0, next));
    commitSeek(duration > 0 ? next / duration : 0);
  };

  const thumbVisible = hover || dragging || liveFraction > 0;

  return (
    <div
      style={{
        marginTop: 14,
        opacity: inert ? 0.55 : 1,
        transition: 'opacity 0.2s',
        userSelect: 'none',
      }}
    >
      {/* track */}
      <div
        ref={trackRef}
        role="slider"
        tabIndex={inert ? -1 : 0}
        aria-label="Audio progress"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration) || 0}
        aria-valuenow={Math.round(fraction * duration) || 0}
        aria-valuetext={`${fmt(fraction * duration)} of ${
          durationKnown ? fmt(duration) : 'streaming'
        }`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
        style={{
          position: 'relative',
          height: 26,
          display: 'flex',
          alignItems: 'center',
          cursor: inert ? 'default' : 'pointer',
          // No `outline: none` here: this element is tabbable and arrow-key
          // seekable, so it needs a visible focus state. A11yStyles supplies
          // the ring via [role="slider"]:focus-visible.
          borderRadius: 999,
          touchAction: 'none',
        }}
      >
        {/* base rail */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: dragging || hover ? 8 : 6,
            borderRadius: 999,
            background: colors.track,
            border: `1px solid ${colors.trackEdge}`,
            overflow: 'hidden',
            transition: 'height 0.12s ease',
          }}
        >
          {/* played fill */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: pct,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${colors.accentSoft}, ${colors.wave})`,
              transition: dragging ? 'none' : 'width 0.12s linear',
            }}
          />
          {/* streaming shimmer at the leading edge — "more audio incoming" */}
          {buffering && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: pct,
                top: 0,
                bottom: 0,
                right: 0,
                // color-mix rather than a literal rgba: the shimmer has to
                // follow --sn-wave through a theme switch like everything else.
                background: `linear-gradient(90deg, color-mix(in srgb, ${colors.wave} 28%, transparent) 0%, color-mix(in srgb, ${colors.wave} 5%, transparent) 40%, color-mix(in srgb, ${colors.wave} 28%, transparent) 80%)`,
                backgroundSize: '200% 100%',
                animation: 'apb-shimmer 1.1s linear infinite',
              }}
            />
          )}
        </div>

        {/* thumb */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: pct,
            width: dragging ? 18 : 14,
            height: dragging ? 18 : 14,
            transform: 'translateX(-50%)',
            borderRadius: '50%',
            background: colors.wave,
            boxShadow: dragging
              ? `0 0 0 6px ${colors.accentSoft}`
              : hover
                ? `0 0 0 4px ${colors.accentSoft}`
                : colors.shadow,
            opacity: thumbVisible ? 1 : 0,
            transition:
              'opacity 0.15s, width 0.12s, height 0.12s, box-shadow 0.12s',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* time row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 6,
          fontSize: 12.5,
          fontVariantNumeric: 'tabular-nums',
          color: colors.textDim,
        }}
      >
        <span style={{ color: fraction > 0 ? colors.accent : colors.textDim }}>
          {fmt(fraction * duration)}
        </span>

        {buffering ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              color: colors.accent,
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: colors.accent,
                animation: 'apb-pulse 1s ease-in-out infinite',
              }}
            />
            {/* Buffering starts at the request, before any audio exists. */}
            {chunksLoaded > 0 ? 'Loading more audio…' : 'Loading audio…'}
          </span>
        ) : (
          <span>{durationKnown ? fmt(duration) : fmt(duration)}</span>
        )}
      </div>

      <style>{`
        @keyframes apb-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes apb-pulse {
          0%, 100% { transform: scale(0.7); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AudioPlayBar;
